import { useEffect, useRef, useState } from "react";
import { Eraser, Pen, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function SignaturePad({ value, signerName, onChange, onSignerName }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState("draw");
  const drawing = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    if (value && value.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.offsetWidth, c.offsetHeight);
      img.src = value;
    }
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); };
  const end = () => { drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange("");
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Button type="button" size="sm" variant={mode === "draw" ? "default" : "outline"} className={mode === "draw" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""} onClick={() => setMode("draw")} data-testid="sig-mode-draw">
          <Pen className="w-3.5 h-3.5 mr-1" /> Draw
        </Button>
        <Button type="button" size="sm" variant={mode === "type" ? "default" : "outline"} className={mode === "type" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""} onClick={() => setMode("type")} data-testid="sig-mode-type">
          <Type className="w-3.5 h-3.5 mr-1" /> Type
        </Button>
        <span className="flex-1" />
        <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid="sig-clear"><Eraser className="w-3.5 h-3.5 mr-1" /> Clear</Button>
      </div>

      {mode === "draw" ? (
        <div className="border border-dashed border-slate-300 rounded-md bg-slate-50">
          <canvas
            ref={canvasRef}
            data-testid="sig-canvas"
            className="w-full h-40 rounded-md touch-none cursor-crosshair block bg-white"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md bg-white p-4">
          <div className="text-3xl italic text-slate-900" style={{ fontFamily: "'Great Vibes', 'Brush Script MT', cursive" }}>
            {signerName || "Your signature"}
          </div>
        </div>
      )}

      <div className="mt-3">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Signed by (name)</label>
        <Input data-testid="signer-name" placeholder="e.g. Jane Roofer" value={signerName || ""} onChange={(e) => onSignerName(e.target.value)} />
      </div>
    </div>
  );
}
